import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import type { KatalogUprawnien, Rola } from '../api/typy';
import { Karta, Komunikat, Ladowanie, Plakietka, Przycisk } from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';

/** Grupujemy uprawnienia po przedrostku — „ruch.wydanie” → sekcja „ruch". */
const NAZWY_GRUP: Record<string, string> = {
  kartoteka: 'Kartoteka', sprzet: 'Sprzęt', lokalizacje: 'Lokalizacje',
  dostawcy: 'Dostawcy', ceny: 'Ceny', ruch: 'Ruch magazynowy',
  zajecia: 'Zajęcia', zestawy: 'Zestawy', rezerwacje: 'Rezerwacje',
  serwis: 'Serwis', inwentaryzacja: 'Inwentaryzacja', raporty: 'Raporty',
  uzytkownicy: 'Użytkownicy', role: 'Role', ustawienia: 'Ustawienia', dziennik: 'Dziennik',
};

export function Role() {
  const [edytowana, setEdytowana] = useState<number | null>(null);

  const zapytanie = useQuery({
    queryKey: ['role'],
    queryFn: () => api.get<{ pozycje: Rola[]; katalogUprawnien: KatalogUprawnien }>('/role'),
  });

  if (zapytanie.isLoading) return <Ladowanie />;
  const { pozycje = [], katalogUprawnien = {} } = zapytanie.data ?? {};

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Role i uprawnienia</h1>
          <p className="mt-1 text-sm text-kamien-500">
            System sprawdza uprawnienia, nie nazwy ról — zmiana zakresu działa natychmiast,
            także na osobach już zalogowanych.
          </p>
        </div>
        <PrzyciskExcel dzial="role" />
      </div>

      {pozycje.map((rola) => (
        <Karta
          key={rola.id}
          tytul={rola.name}
          opis={rola.description ?? undefined}
          akcje={
            <div className="flex items-center gap-3">
              <Plakietka>{rola._count.users} {odmianaKont(rola._count.users)}</Plakietka>
              <Plakietka ton={rola.code === 'ADMIN' ? 'blad' : 'neutralny'}>
                {rola.permissions.length} uprawnień
              </Plakietka>
              <Przycisk
                wariant="cichy" className="min-h-9 px-2 text-xs"
                onClick={() => setEdytowana(edytowana === rola.id ? null : rola.id)}
              >
                {edytowana === rola.id ? 'Zwiń' : 'Zmień zakres'}
              </Przycisk>
            </div>
          }
        >
          {edytowana === rola.id
            ? <EdycjaRoli rola={rola} katalog={katalogUprawnien} zamknij={() => setEdytowana(null)} />
            : <PodgladUprawnien uprawnienia={rola.permissions} katalog={katalogUprawnien} />}
        </Karta>
      ))}
    </div>
  );
}

function odmianaKont(n: number) {
  if (n === 1) return 'konto';
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return 'konta';
  return 'kont';
}

function grupuj(kody: string[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const k of kody) {
    const grupa = k.split('.')[0] ?? 'inne';
    (out[grupa] ??= []).push(k);
  }
  return out;
}

function PodgladUprawnien({ uprawnienia, katalog }: {
  uprawnienia: string[]; katalog: KatalogUprawnien;
}) {
  const grupy = grupuj(uprawnienia);
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-4 px-5 py-4">
      {Object.entries(grupy).map(([grupa, kody]) => (
        <div key={grupa}>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-kamien-500">
            {NAZWY_GRUP[grupa] ?? grupa}
          </p>
          <ul className="text-sm text-kamien-700">
            {kody.map((k) => <li key={k}>{katalog[k] ?? k}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

function EdycjaRoli({ rola, katalog, zamknij }: {
  rola: Rola; katalog: KatalogUprawnien; zamknij: () => void;
}) {
  const qc = useQueryClient();
  const [zaznaczone, setZaznaczone] = useState<Set<string>>(new Set(rola.permissions));
  const [blad, setBlad] = useState<string | null>(null);

  const zapisz = useMutation({
    mutationFn: () =>
      api.put(`/role/${rola.id}`, {
        name: rola.name,
        description: rola.description ?? '',
        permissions: [...zaznaczone],
        version: rola.version,
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['role'] }); zamknij(); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać.'),
  });

  const grupy = grupuj(Object.keys(katalog));

  return (
    <div className="flex flex-col gap-4 px-5 py-4">
      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(grupy).map(([grupa, kody]) => (
          <fieldset key={grupa}>
            <legend className="mb-1.5 text-xs font-medium uppercase tracking-wide text-kamien-500">
              {NAZWY_GRUP[grupa] ?? grupa}
            </legend>
            <div className="flex flex-col gap-1.5">
              {kody.map((k) => (
                <label key={k} className="flex cursor-pointer items-start gap-2 text-sm text-kamien-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-bordo-600"
                    checked={zaznaczone.has(k)}
                    onChange={(e) => {
                      const n = new Set(zaznaczone);
                      if (e.target.checked) n.add(k); else n.delete(k);
                      setZaznaczone(n);
                    }}
                  />
                  <span>{katalog[k]}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="flex gap-3 border-t border-kamien-200 pt-4">
        <Przycisk wariant="glowny" onClick={() => zapisz.mutate()} disabled={zapisz.isPending}>
          {zapisz.isPending ? 'Zapisywanie...' : 'Zapisz zakres'}
        </Przycisk>
        <Przycisk wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
      </div>
    </div>
  );
}
